import { ObjectData, ObjectId } from "@novorender/data-js-api";
import { ReadonlyVec3 } from "gl-matrix";

import { procoreClient } from "./client";

/**
 * Procore API wraps responses in a data envelope
 */
interface BimObjectMetadataApiResponse {
    data: BimObjectMetadataResponse;
}

/**
 * Response from BIM Search DB API for object metadata
 * Matches MetadataResponse in ProcoreResponses.cs
 */
interface BimObjectMetadataResponse {
    id: string;
    path: string;
    name: string;
    type: number;
    bounds?: {
        sphere: {
            center: ReadonlyVec3;
            radius: number;
        };
        box: {
            min: ReadonlyVec3;
            max: ReadonlyVec3;
        };
    };
    properties: [string, string][];
}

/**
 * Fetch metadata for a single object from Procore BIM Search DB API
 *
 * @param params - Request parameters including scene context and object ID
 * @returns ObjectData compatible with Novorender SDK
 */
export async function getProcoreObjectMetadata(params: {
    companyId: number;
    projectId: number;
    sceneId: string;
    sceneVersionId: string;
    objectId: number;
}): Promise<ObjectData> {
    const apiResponse = await procoreClient<BimObjectMetadataApiResponse>(
        `/rest/v2.0/companies/${params.companyId}/projects/${params.projectId}/bim/scenes/${params.sceneId}/versions/${params.sceneVersionId}/objects/${params.objectId}`,
        {
            method: "GET",
        },
    );

    return transformToObjectData(apiResponse.data, params.objectId);
}

/**
 * Transform BIM Search DB metadata response to Novorender ObjectData format
 */
function transformToObjectData(response: BimObjectMetadataResponse, originalId: number): ObjectData {
    const id = parseInt(response.id, 10) || originalId;

    const base: ObjectData = {
        id: id as ObjectId,
        name: response.name || "",
        path: response.path || "/",
        type: response.type ?? 1,
        properties: response.properties || [],
        bounds: response.bounds,
        async loadMetaData() {
            return base;
        },
        async save() {
            // Read-only for Procore backend
            return false;
        },
    };

    return base;
}
